import {
	createLog,
	LogSeverity,
	OPERATIONS_SECTIONS,
	Services,
	type ServicesProvider,
	SETTINGS_GROUPS,
	type TitlingServiceConfig,
	type TitlingServiceConfigDtoInput,
	type TitlingServiceConfigDtoOutput,
	TitlingServiceConfigDtoSchema,
} from '@ekhodealer/ekho-common';
import { Note } from '@ekhodealer/ekho-common/rev';

import { useCurrentServicesProvider } from '@common/hooks/useCurrentServicesProvider';

import { useUpdateTitlingService } from '../../../api/configuration/services/useUpdateTitlingService';
import { useServiceProgramInfo } from '../../../hooks/useServiceProgramInfo';
import { FormSection } from '../../config-mgmt/FormSection';

/**
 * Helper function to get initial form data from ServicesProvider
 */
function getDefaultValues(
	servicesProvider: ServicesProvider | undefined | null,
): TitlingServiceConfigDtoInput {
	const titlingConfig = servicesProvider?.config?.[Services.TITLING] as
		| TitlingServiceConfig
		| undefined;

	return {
		supported: titlingConfig?.supported ?? false,
	};
}

export function TitlingServiceForm() {
	const { data: servicesProvider } = useCurrentServicesProvider();
	const programInfo = useServiceProgramInfo(OPERATIONS_SECTIONS.TITLING);
	const updateTitlingServiceMutation = useUpdateTitlingService();

	// Early return if service is not part of the program
	if (!programInfo.shouldRender) {
		return null;
	}

	// Handle form submit
	const handleSubmit = async (
		data: TitlingServiceConfigDtoOutput,
	): Promise<{ isSuccess: boolean }> => {
		if (!servicesProvider) return { isSuccess: false };

		try {
			await updateTitlingServiceMutation.mutateAsync({
				supported: data.supported,
			});
			return { isSuccess: true };
		} catch (error) {
			createLog(
				'config-mgmt',
				`Failed to update titling service configuration: ${
					error instanceof Error ? error.message : String(error)
				}`,
				LogSeverity.ERROR,
			);
			return { isSuccess: false };
		}
	};

	return (
		<FormSection
			sectionPath={{
				group: SETTINGS_GROUPS.OPERATIONS,
				section: OPERATIONS_SECTIONS.TITLING,
			}}
			form={{
				schema: TitlingServiceConfigDtoSchema,
				defaultValues: getDefaultValues(servicesProvider),
				onSubmit: handleSubmit,
			}}
			title='Titling'
			description='Enable Ekho-managed vehicle title processing. When enabled, Ekho handles title transfers on your behalf after the sale is completed.'
			formTitle='Titling service'
			toggleSettings={{
				fieldName: 'supported',
				visibility: 'write',
			}}
			hideDivider>
			<Note>No configuration needed for this service</Note>
		</FormSection>
	);
}

export default TitlingServiceForm;
